import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Typography, LinearProgress, Snackbar, Alert, Chip, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  MarkerType
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import StoryboardNode from './StoryboardNodeWithTabs';
import api from '../api/axios';
import { countOverallStoryboardProgress, findNextIncompleteEloModule } from '../utils/storyboardTabValidation';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';

/**
 * Simple storyboard canvas using React Flow.
 * - Add nodes
 * - Connect nodes (drag from one to another)
 * - Pan/zoom via built-in controls
 */
function StoryBoardFlowInner({ qualificationId }) {
  const idCounterRef = useRef(1);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { screenToFlowPosition, fitView, setViewport } = useReactFlow();
  const [saving, setSaving] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [reqByCode, setReqByCode] = useState({});
  const [outcomesById, setOutcomesById] = useState({});
  const [assignedMap, setAssignedMap] = useState({});
  const [tasksModal, setTasksModal] = useState({ open: false, title: '', assigned: { KT: [], PS: [], WE: [] } });
  const [progressToast, setProgressToast] = useState({ open: false, message: '', total: 0, completed: 0 });
  const [exportToast, setExportToast] = useState({ open: false, message: '', severity: 'info' });
  const [progressPanelOpen, setProgressPanelOpen] = useState(true);
  const [zoomEnabled, setZoomEnabled] = useState(true);
  
  // Share functionality
  const isReadOnly = (() => { 
    try { 
      const url = new URL(window.location.href); 
      return url.searchParams.get('readonly') === '1'; 
    } catch { 
      return false; 
    } 
  })();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [qualificationTitle, setQualificationTitle] = useState('');

  // Load qualification title for read-only view
  useEffect(() => {
    if (!qualificationId || !isReadOnly) return;
    (async () => {
      try {
        const res = await api.get(`/qualifications/${qualificationId}`);
        setQualificationTitle(res.data?.title || '');
      } catch {}
    })();
  }, [qualificationId, isReadOnly]);

  // Load nodes (autogenerate if empty)
  useEffect(() => {
    if (!qualificationId) return;
    (async () => {
      try {
        // Run server-side dedupe before fetching
        await api.post(`/qualifications/${qualificationId}/storyboard-nodes/dedupe`);
      } catch {}
      try {

        const [nodesRes, reqRes, outRes] = await Promise.all([
          api.get(`/qualifications/${qualificationId}/storyboard-nodes`),
          api.get(`/qualifications/${qualificationId}/requirement-nodes`),
          api.get(`/qualifications/${qualificationId}/outcomes`)
        ]);
        const reqNodes = Array.isArray(reqRes.data?.nodes) ? reqRes.data.nodes : [];
        const reqMap = reqNodes.reduce((acc, n) => { if (n.code) acc[n.code] = n; return acc; }, {});
        setReqByCode(reqMap);
        const outRows = Array.isArray(outRes.data?.outcomes) ? outRes.data.outcomes : [];

        console.log(`[StoryBoardFlow] Initial fetch - Outcomes: ${outRows.length}, Nodes: ${nodesRes.data?.nodes?.length || 0}`);
        if (outRows[0]) {
          // Found outcome row
        }
        const outMap = outRows.reduce((acc, o) => { acc[o.id] = o; return acc; }, {});
        setOutcomesById(outMap);

        let list = Array.isArray(nodesRes.data?.nodes) ? nodesRes.data.nodes : [];
        console.log(`[StoryBoardFlow] Initial nodes from GET: ${list.length}`, list.map(n => ({ id: n.id, label: n.label, outcome_id: n.outcome_id, is_orientation: n.data?.is_orientation, is_eisa: n.data?.is_eisa })));

        // Check if we have a complete set of nodes
        // We should have: 1 Orientation + N ELO nodes (where N = number of outcomes) + 1 EISA = at least 2 + outcomes.length
        const hasOrientation = list.some(n => n.data?.is_orientation === true || n.data?.uses_curriculum_doc === true);
        const hasEisa = list.some(n => n.data?.is_eisa === true);
        const eloNodes = list.filter(n => !n.data?.is_orientation && !n.data?.is_eisa && !n.data?.uses_curriculum_doc);
        const expectedMinNodes = outRows.length > 0 ? 2 + outRows.length : 2; // Orientation + ELOs + EISA
        
        console.log(`[StoryBoardFlow] Node completeness check:`, {
          total: list.length,
          expectedMin: expectedMinNodes,
          hasOrientation,
          hasEisa,
          eloNodes: eloNodes.length,
          outcomes: outRows.length,
          incomplete: list.length < expectedMinNodes || !hasOrientation || !hasEisa || eloNodes.length < outRows.length
        });

        // Force autogenerate if:
        // 1. No nodes exist, OR
        // 2. We have fewer nodes than expected (incomplete set), OR
        // 3. Missing Orientation or EISA nodes
        if (!list || list.length === 0 || list.length < expectedMinNodes || !hasOrientation || !hasEisa || eloNodes.length < outRows.length) {
          console.log('[StoryBoardFlow] No nodes found, calling autogenerate...');
          try {
            const gen = await api.post(`/qualifications/${qualificationId}/storyboard-nodes/autogenerate`);
            console.log('[StoryBoardFlow] Autogenerate response:', gen.data);
            list = Array.isArray(gen.data?.nodes) ? gen.data.nodes : [];
            console.log('[StoryBoardFlow] Nodes after autogenerate:', list.length);
            console.log('[StoryBoardFlow] Node details:', list.map(n => ({ id: n.id, label: n.label, outcome_id: n.outcome_id, is_orientation: n.data?.is_orientation, is_eisa: n.data?.is_eisa })));
            if (list.length === 0) {
              console.error('[StoryBoardFlow] WARNING: Autogenerate returned empty nodes array!');
            } else if (list.length === 1) {
              console.warn('[StoryBoardFlow] WARNING: Only 1 node returned! Expected Orientation + ELO nodes + EISA. Check backend logs.');
            }
          } catch (error) {
            console.error('[StoryBoardFlow] ERROR: Autogenerate failed:', error);
            // Don't silently fail - show error to user
            setProgressToast({
              open: true,
              message: `❌ Failed to generate storyboard nodes: ${error.response?.data?.message || error.message}`,
              total: 0,
              completed: 0
            });
          }
        }
        // Sort nodes to match ELO canvas column order with Orientation first
        // Build outcome order maps: outcome.id -> array index AND elo_number -> array index
        const outOrderById = new Map();
        const outOrderByEloNumber = new Map();
        outRows.forEach((o, idx) => { 
          if (o && o.id != null) {
            outOrderById.set(Number(o.id), idx);
          }
          if (o && o.elo_number) {
            // Match by elo_number (e.g., "ELO1", "ELO2", etc.) - normalize to uppercase
            outOrderByEloNumber.set(String(o.elo_number).toUpperCase().trim(), idx);
          }
        });
        
        // Debug logging to verify outcome order
        
        // Build sort metadata for each node
        const nodeSortData = list.map(node => {
          // Orientation detection: A node is orientation if:
          // 1. It has explicit orientation flags, OR
          // 2. It has label "Orientation" (not "ELO1", "ELO2", etc.) AND no outcome_id
          // Note: Nodes with ELO labels (ELO1, ELO2, etc.) are ELO nodes, even if outcome_id is null
          const hasOrientationFlag = node?.data?.is_orientation === true || node?.data?.uses_curriculum_doc === true;
          const isEloLabel = node.label && /^ELO\d+$/i.test(String(node.label).trim());
          const isOrientationLabel = String(node.label || '').trim().toLowerCase() === 'orientation';
          const isOri = hasOrientationFlag || (isOrientationLabel && !node.outcome_id && !isEloLabel);
          
          let outcomeIdx = Number.MAX_SAFE_INTEGER;
          
          if (!isOri) {
            if (node.outcome_id && outOrderById.has(Number(node.outcome_id))) {
              outcomeIdx = outOrderById.get(Number(node.outcome_id));
            } else if (node.label) {
              const eloMatch = String(node.label).match(/ELO\d+/i);
              if (eloMatch && outOrderByEloNumber.has(eloMatch[0].toUpperCase())) {
                outcomeIdx = outOrderByEloNumber.get(eloMatch[0].toUpperCase());
              }
            }
          }
          
          return { node, isOri, outcomeIdx };
        });
        
        // Sort the metadata array - CRITICAL: Use explicit sorting with detailed logging

        nodeSortData.forEach((item, idx) => {

        });
        
        // Sort using a stable algorithm - sort by outcomeIdx for ELO nodes
        nodeSortData.sort((a, b) => {
          // Rule 1: Orientation nodes go first
          if (a.isOri && !b.isOri) return -1;
          if (!a.isOri && b.isOri) return 1;
          
          // Rule 2: Both are orientation - keep original order
          if (a.isOri && b.isOri) {
            const ax = typeof a.node.position_x === 'number' ? a.node.position_x : 0;
            const bx = typeof b.node.position_x === 'number' ? b.node.position_x : 0;
            if (ax !== bx) return ax - bx;
            return (a.node.id || 0) - (b.node.id || 0);
          }
          
          // Rule 3: Both are ELO nodes - sort by outcomeIdx (lower index first)
          const compareResult = a.outcomeIdx - b.outcomeIdx;
          return compareResult;
        });
        

        nodeSortData.forEach((item, idx) => {

        });
        
        // Replace list with sorted nodes
        list.length = 0;
        const sortedNodes = nodeSortData.map(item => item.node);
        list.push(...sortedNodes);
        
        // Explicit verification of sort

        // Horizontal journey layout: single row with large spacing
        const spacingX = 1400;
        const spacingY = 900; // unused for single row but kept for readability
        const cols = Number.MAX_SAFE_INTEGER; // force single row
        // Build a map of outcomes by elo_number for title syncing
        const outMapByEloNumber = {};
        outRows.forEach(o => {
          if (o && o.elo_number) {
            outMapByEloNumber[String(o.elo_number).toUpperCase().trim()] = o;
          }
        });
        
        const rfNodes = list.map((n, idx) => {
          const col = idx; // single row
          const row = 0;
          const x = col * spacingX + 80;
          const y = row * spacingY + 80;
          
          // Try to get ELO data by outcome_id first, then by elo_number from label
          let eloData = outMap[n.outcome_id];
          if (!eloData && n.label) {
            const eloMatch = String(n.label).match(/ELO\d+/i);
            if (eloMatch) {
              eloData = outMapByEloNumber[eloMatch[0].toUpperCase()];
            }
          }
          
          // Keep the assignment-screen module name; do not keep bare ELO1 labels
          let syncedTitle = n.data?.title;
          if (eloData) {
            const moduleName = outcomeModuleDisplayName(eloData);
            if (moduleName && moduleName !== syncedTitle) {
              syncedTitle = moduleName;
            }
          }
          
          // Calculate module index: Orientation = 0, ELO modules = 1, 2, 3..., EISA = last
          // But display as 1-indexed (Module 1, Module 2, etc.)
          const nodeData = n.data || {};
          const isOrientation = nodeData.is_orientation === true || nodeData.uses_curriculum_doc === true;
          const isEisa = nodeData.is_eisa === true;
          let moduleIndex = idx + 1; // Default to array position + 1
          
          // If it's orientation, it should be Module 1 (or 0 if we want 0-indexed)
          // If it's EISA, it should be the last module number
          // ELO modules should be numbered sequentially after orientation
          
          return {
            id: String(n.id),
            position: { x, y },
            data: { 
              ...n.data, 
              title: syncedTitle, // Use synced title
              _dbId: n.id, 
              _outcomeId: n.outcome_id, 
              _moduleIndex: moduleIndex, // Always use calculated index
              _qualificationId: qualificationId,
              _eloData: eloData // Include enriched ELO data
            },
            type: 'storyboard',
          };
        });
        console.log('[StoryBoardFlow] Setting nodes:', rfNodes.length, 'nodes');
        if (rfNodes.length === 0) {
          console.error('[StoryBoardFlow] ERROR: No nodes to display! Check autogenerate endpoint.');
        }
        // Kick off sequential auto-generation from the first node (orientation)
        const nodesWithAutoGen = rfNodes.map((n, idx) => ({
          ...n,
          data: {
            ...n.data,
            ...(idx === 0 ? { _triggerAutoGen: Date.now() } : {})
          }
        }));
        setNodes(nodesWithAutoGen);
        // Link nodes sequentially by ELO order
        const rfEdges = rfNodes.slice(1).map((node, i) => ({ id: `e-${rfNodes[i].id}-${node.id}`, source: rfNodes[i].id, target: node.id, sourceHandle: 'r', targetHandle: 'l', markerEnd: { type: MarkerType.ArrowClosed, color: '#7c3aed' }, type: 'smoothstep', animated: true, label: `Step ${i+1} → ${i+2}`, labelBgPadding: [6,3], labelBgBorderRadius: 4, labelStyle: { fill: '#fff' }, style: { stroke: '#7c3aed', strokeWidth: 2 } }));
        setEdges(rfEdges);
        // Expand view first, then fit to nodes to ensure wide bounds
        requestAnimationFrame(() => {
          try { setViewport({ x: -200, y: -200, zoom: 0.5 }, { duration: 0 }); } catch {}
          requestAnimationFrame(() => {
            try { fitView({ padding: 0.5, includeHiddenNodes: true, duration: 300 }); } catch {}
          });
        });
        // Build assignment details per node using new enriched assignment data
        const assigned = {};
        rfNodes.forEach(n => {
          const out = outMap[n.data?._outcomeId];
          const pack = { KT: [], PS: [], WE: [] };
          if (out) {
            // Use new enriched assignment data if available
            if (out.assigned) {
              // Direct assignment from enriched ELO data
              if (Array.isArray(out.assigned.KT)) {
                pack.KT = out.assigned.KT.map(item => ({
                  code: item.code,
                  title: item.title,
                  credits: item.credits,
                  notional_hours: item.notional_hours,
                  active_hours: item.active_hours,
                  weight_pct: item.weight_pct,
                  reason: item.reason,
                  confidence: item.confidence
                }));
              }
              if (Array.isArray(out.assigned.PS)) {
                pack.PS = out.assigned.PS.map(item => ({
                  code: item.code,
                  title: item.title,
                  credits: item.credits,
                  notional_hours: item.notional_hours,
                  active_hours: item.active_hours,
                  weight_pct: item.weight_pct,
                  reason: item.reason,
                  confidence: item.confidence
                }));
              }
              if (Array.isArray(out.assigned.WE)) {
                pack.WE = out.assigned.WE.map(item => ({
                  code: item.code,
                  title: item.title,
                  credits: item.credits,
                  notional_hours: item.notional_hours,
                  active_hours: item.active_hours,
                  weight_pct: item.weight_pct,
                  reason: item.reason,
                  confidence: item.confidence
                }));
              }
            } else {
              // Fallback to old structure for backward compatibility
              const add = (arr, type) => {
                if (Array.isArray(arr)) {
                  arr.forEach(code => {
                    const node = reqMap[typeof code === 'string' ? code : (code && code.code)];
                    if (node) {
                      const item = buildRequirementTree(node, reqNodes);
                      pack[type].push(item);
                    }
                  });
                }
              };
              add(out.knowledge_tasks, 'KT');
              add(out.practical_work, 'PS');
              add(out.workplace_experience, 'WE');
            }
          }
          assigned[n.id] = pack;
        });
        setAssignedMap(assigned);
      } catch {}
    })();
  }, [qualificationId, setNodes]);

  const buildRequirementTree = (root, all) => {
    const children = all.filter(n => n.parent_code && root.code && n.parent_code === root.code);
    return {
      id: root.id,
      node_type: root.node_type,
      code: root.code,
      title: root.title,
      text: root.text,
      weight_pct: root.weight_pct,
      credits: root.credits,
      derived_credits: root.derived_credits,
      notional_hours: root.notional_hours,
      active_hours: root.active_hours,
      nqf_level: root.nqf_level,
      evidence_type: root.evidence_type,
      children: children.map(ch => buildRequirementTree(ch, all))
    };
  };

  const onConnect = useCallback((connection) => {
    setEdges((eds) => addEdge({ ...connection, animated: false }, eds));
  }, [setEdges]);

  const addNodeAtCenter = useCallback(() => {
    const center = { x: window.innerWidth / 2, y: (window.innerHeight - 64) / 2 };
    const position = screenToFlowPosition(center);
    const nextId = `N-${idCounterRef.current++}`;
    setNodes((nds) => nds.concat({ id: nextId, position, data: { label: `Node ${nextId}` } }));
  }, [screenToFlowPosition, setNodes]);

  // Removed onPaneDoubleClick - not supported in current ReactFlow version
  // If needed, can be re-implemented using onPaneClick with double-click detection

  const onNodeDataChange = useCallback((id, data) => {
    setNodes(nds => {
      // Find the node's index in the sorted array to preserve _moduleIndex
      const nodeIndex = nds.findIndex(n => n.id === id);
      const updatedNodes = nds.map((n, idx) => {
        if (n.id === id) {
          return { 
            ...n, 
            data: { 
              ...data, 
              // Preserve _moduleIndex if it exists, otherwise calculate from position
              _moduleIndex: data._moduleIndex || (nodeIndex >= 0 ? nodeIndex + 1 : idx + 1),
              _dbId: data._dbId || n.data?._dbId,
              _qualificationId: data._qualificationId || n.data?._qualificationId,
              _outcomeId: data._outcomeId || n.data?._outcomeId
            } 
          };
        }
        return n;
      });
      return updatedNodes;
    });
  }, [setNodes]);

  const openTasks = useCallback(({ title, assigned }) => {
    setTasksModal({ open: true, title, assigned: assigned || { KT: [], PS: [], WE: [] } });
  }, []);

  // Calculate overall progress across all ELO modules (orientation/EISA excluded)
  const overallProgress = useMemo(() => countOverallStoryboardProgress(nodes), [nodes]);

  // Handle module completion and auto-advance to next incomplete module
  const handleModuleComplete = useCallback((completedId, completedIndex) => {
    const progress = countOverallStoryboardProgress(nodes);
    const nextIncomplete = findNextIncompleteEloModule(nodes, completedId);

    if (nextIncomplete) {
      setProgressToast({
        open: true,
        message: `Continuing: ${nextIncomplete.data?.title || nextIncomplete.label} (${progress.completed}/${progress.total} tabs complete)`,
        total: progress.total,
        completed: progress.completed
      });

      setTimeout(() => {
        setNodes((currentNodes) =>
          currentNodes.map((n) => {
            if (n.id === nextIncomplete.id) {
              return {
                ...n,
                data: { ...n.data, _triggerAutoGen: Date.now() }
              };
            }
            return n;
          })
        );
      }, 2000);
      return;
    }

    if (progress.isComplete) {
      setProgressToast({
        open: true,
        message: '🎉 All tabs complete! 100% generated.',
        total: progress.total,
        completed: progress.completed
      });
    } else {
      setProgressToast({
        open: true,
        message: `Generation pass finished — ${progress.completed}/${progress.total} tabs complete. Retry missing tabs manually or refresh to continue.`,
        total: progress.total,
        completed: progress.completed
      });
    }
  }, [nodes, setNodes]);

  // Memoize nodeTypes to prevent React Flow warnings and re-render loops
  // CRITICAL: Define nodeTypes OUTSIDE useMemo to prevent remounting on every state change
  const nodeTypes = useMemo(
    () => ({
      storyboard: StoryboardNode, // Pass component directly, not wrapped in arrow function
    }),
    [] // Empty deps - only create once
  );
  
  // Node-specific props passed via nodes array
  const nodesWithProps = useMemo(() => {
    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        _onChange: onNodeDataChange,
        _assigned: assignedMap[node.id],
        _onOpenTasks: openTasks,
        _onModuleComplete: handleModuleComplete,
        _isReadOnly: isReadOnly // Pass read-only flag to node
      }
    }));
  }, [nodes, onNodeDataChange, assignedMap, openTasks, handleModuleComplete, isReadOnly]);

  const saveAll = useCallback(async () => {
    if (!qualificationId) return;
    setSaving(true);
    try {
      const payload = nodes.map(n => ({
        id: Number(n.data?._dbId) || undefined,
        outcome_id: Number(n.data?._outcomeId) || undefined,
        position: n.position,
        width: n.width,
        height: n.height,
        label: n.data?.label || undefined,
        data: { ...n.data }
      }));
      payload.forEach(p => { if (p.data) { delete p.data._dbId; delete p.data._outcomeId; } });
      await api.post(`/qualifications/${qualificationId}/storyboard-nodes`, { nodes: payload });
    } catch {}
    setSaving(false);
  }, [qualificationId, nodes]);

  // Clear all nodes and restart generation
  const clearAllModules = useCallback(async () => {
    if (!qualificationId) return;
    
    const confirmed = window.confirm(
      'This will DELETE ALL nodes and restart the generation process from scratch. Are you sure?'
    );
    if (!confirmed) return;
    
    setClearingAll(true);
    try {
      // Delete all nodes from backend
      await api.delete(`/qualifications/${qualificationId}/storyboard-nodes`);
      
      // Clear local state
      setNodes([]);
      
      // Show success toast
      setProgressToast({
        open: true,
        message: '🗑️ All nodes deleted! Refreshing page to restart generation...',
        total: 0,
        completed: 0
      });
      
      // Reload page after 2 seconds to restart auto-generation
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('[clearAllModules] Error:', error);
      setProgressToast({
        open: true,
        message: '❌ Failed to delete all nodes: ' + (error.response?.data?.message || error.message),
        total: 0,
        completed: 0
      });
    } finally {
      setClearingAll(false);
    }
  }, [qualificationId, setNodes]);

  const [generatingAll, setGeneratingAll] = useState(false);
  
  const generateAllMissing = useCallback(async () => {
    setGeneratingAll(true);
    try {
      // Find all modules missing AI content
      const missingContent = nodes.map(n => ({
        id: n.id,
        dbId: n.data?._dbId,
        needsReframes: !n.data?.ai_reframes,
        needsCoreOutcomes: !n.data?.ai_core_outcomes,
        needsLearningOutcomes: !n.data?.ai_learning_outcomes
      })).filter(m => m.needsReframes || m.needsCoreOutcomes || m.needsLearningOutcomes);
      

      
      // Generate sequentially to avoid rate limits
      for (const module of missingContent) {
        if (module.needsReframes) {

          await api.post(`/qualifications/${qualificationId}/storyboard-nodes/${module.dbId}/generate-reframes`);
          await new Promise(r => setTimeout(r, 2000)); // Wait between requests
        }
        if (module.needsCoreOutcomes) {
          await api.post(`/qualifications/${qualificationId}/storyboard-nodes/${module.dbId}/generate-core-outcomes`);
          await new Promise(r => setTimeout(r, 2000));
        }
        if (module.needsLearningOutcomes) {
          await api.post(`/qualifications/${qualificationId}/storyboard-nodes/${module.dbId}/generate-learning-outcomes`);
          await new Promise(r => setTimeout(r, 2000));
        }
      }
      
      // Reload nodes after batch generation - need to sort by outcomes order
      const [nodesRes, outRes] = await Promise.all([
        api.get(`/qualifications/${qualificationId}/storyboard-nodes`),
        api.get(`/qualifications/${qualificationId}/outcomes`)
      ]);
      const list = Array.isArray(nodesRes.data?.nodes) ? nodesRes.data.nodes : [];
      const outRows = Array.isArray(outRes.data?.outcomes) ? outRes.data.outcomes : [];
      
      // Build outcome order maps: outcome.id -> array index AND elo_number -> array index
      const outOrderById = new Map();
      const outOrderByEloNumber = new Map();
      outRows.forEach((o, idx) => { 
        if (o && o.id != null) {
          outOrderById.set(Number(o.id), idx);
        }
        if (o && o.elo_number) {
          outOrderByEloNumber.set(String(o.elo_number).toUpperCase().trim(), idx);
        }
      });
      
      // Sort to match outcomes order (orientation first, then by outcome index)
      list.sort((a, b) => {
        const aIsOri = !a.outcome_id || a?.data?.is_orientation === true || a?.data?.uses_curriculum_doc === true;
        const bIsOri = !b.outcome_id || b?.data?.is_orientation === true || b?.data?.uses_curriculum_doc === true;
        if (aIsOri && !bIsOri) return -1;
        if (!aIsOri && bIsOri) return 1;
        if (aIsOri && bIsOri) {
          const ax = typeof a.position_x === 'number' ? a.position_x : 0;
          const bx = typeof b.position_x === 'number' ? b.position_x : 0;
          return ax !== bx ? ax - bx : (a.id || 0) - (b.id || 0);
        }
        
        // Try matching by outcome_id first, then by elo_number from label
        let aIdx = Number.MAX_SAFE_INTEGER;
        let bIdx = Number.MAX_SAFE_INTEGER;
        
        if (a.outcome_id && outOrderById.has(Number(a.outcome_id))) {
          aIdx = outOrderById.get(Number(a.outcome_id));
        } else if (a.label) {
          const aEloMatch = String(a.label).match(/ELO\d+/i);
          if (aEloMatch && outOrderByEloNumber.has(aEloMatch[0].toUpperCase())) {
            aIdx = outOrderByEloNumber.get(aEloMatch[0].toUpperCase());
          }
        }
        
        if (b.outcome_id && outOrderById.has(Number(b.outcome_id))) {
          bIdx = outOrderById.get(Number(b.outcome_id));
        } else if (b.label) {
          const bEloMatch = String(b.label).match(/ELO\d+/i);
          if (bEloMatch && outOrderByEloNumber.has(bEloMatch[0].toUpperCase())) {
            bIdx = outOrderByEloNumber.get(bEloMatch[0].toUpperCase());
          }
        }
        
        return aIdx !== bIdx ? aIdx - bIdx : (a.id || 0) - (b.id || 0);
      });
      
      const outById = new Map(outRows.filter((o) => o && o.id != null).map((o) => [Number(o.id), o]));
      const outByElo = new Map(
        outRows
          .filter((o) => o && o.elo_number)
          .map((o) => [String(o.elo_number).toUpperCase().trim(), o])
      );
      setNodes(list.map((n, idx) => {
        let eloData = n.outcome_id != null ? outById.get(Number(n.outcome_id)) : null;
        if (!eloData && n.label) {
          const eloMatch = String(n.label).match(/ELO\d+/i);
          if (eloMatch) eloData = outByElo.get(eloMatch[0].toUpperCase());
        }
        const syncedTitle = eloData ? outcomeModuleDisplayName(eloData) : n.data?.title;
        return {
          id: String(n.id),
          position: nodes.find(existing => existing.id === String(n.id))?.position || { x: idx * 1400 + 80, y: 80 },
          data: { ...n.data, title: syncedTitle || n.data?.title, _dbId: n.id, _qualificationId: qualificationId, _outcomeId: n.outcome_id, _moduleIndex: idx + 1 },
          type: 'storyboard'
        };
      }));
    } catch (error) {

    } finally {
      setGeneratingAll(false);
    }
  }, [nodes, qualificationId, setNodes]);

  const containerSx = useMemo(() => ({ width: '100%', height: '100%', position: 'relative', background: '#0f0f12' }), []);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Overall Progress Indicator */}
      {progressPanelOpen && !isReadOnly && (
        <Box sx={{ 
          position: 'absolute', 
          top: 10, 
          right: 10, 
          zIndex: 20, 
          bgcolor: 'rgba(255, 255, 255, 0.95)', 
          p: 2, 
          borderRadius: 2, 
          boxShadow: 3,
          minWidth: 280
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Overall Progress
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setProgressPanelOpen(false)}
              sx={{ p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: overallProgress.percent === 100 ? '#10b981' : '#3b82f6' }}>
            {overallProgress.percent}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {overallProgress.completed} / {overallProgress.total} tabs complete
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={overallProgress.percent} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            '& .MuiLinearProgress-bar': {
              bgcolor: overallProgress.percent === 100 ? '#10b981' : '#3b82f6'
            }
          }} 
        />
        <Box sx={{ mt: 1.5, maxHeight: 150, overflowY: 'auto' }}>
          {overallProgress.modules.map(mod => (
            <Box key={mod.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#475569' }}>
                {mod.title}
              </Typography>
              <Chip 
                label={`${mod.completed}/${mod.total}`} 
                size="small" 
                color={mod.completed === mod.total ? 'success' : 'default'}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            </Box>
          ))}
        </Box>
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 10, left: 10, zIndex: 20 }}>
        {!isReadOnly && (
          <>
            <Button 
              variant="contained" 
              size="small" 
              color="success"
              onClick={async () => {
                try {
                  await api.put(`/qualifications/${qualificationId}/steps/step_4`, { status: 'completed' });
                  await api.put(`/qualifications/${qualificationId}/steps/step_5`, { status: 'not_started' });
                  // Navigate to step 5
                  window.location.href = `/qualification/${qualificationId}#step_5`;
                  setTimeout(() => {
                    try {
                      const event = new CustomEvent('navigateToStep', { detail: { step: 'step_5' } });
                      window.dispatchEvent(event);
                    } catch {}
                  }, 100);
                } catch (e) {

                  alert('Failed to mark step complete');
                }
              }}
              sx={{ fontWeight: 700 }}
            >
              ✓ Complete & Continue to Content Creation
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              color="error"
              onClick={clearAllModules}
              disabled={clearingAll}
              sx={{ fontWeight: 600 }}
            >
              {clearingAll ? 'Clearing All...' : 'Clear All Modules & Restart'}
            </Button>
          </>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<ShareIcon />}
          onClick={async () => {
            try {
              const res = await api.post(`/qualifications/${qualificationId}/share-token`);
              const token = res.data?.token;
              const url = new URL(window.location.href);
              url.searchParams.set('readonly', '1');
              url.searchParams.set('view', 'storyboard');
              if (token) url.searchParams.set('token', token);
              setShareLink(url.toString());
              setShareDialogOpen(true);
            } catch {}
          }}
        >
          Share
        </Button>
        {isReadOnly && qualificationTitle && (
          <Typography variant="subtitle2" sx={{ fontWeight: 800, p: 1 }}>
            {qualificationTitle}
          </Typography>
        )}
        <Button variant="outlined" size="small" color="primary" onClick={async () => {
          try {
            setExportToast({ open: true, message: 'Generating PDF...', severity: 'info' });
            const response = await api.post(`/qualifications/${qualificationId}/storyboard-nodes/export-pdf`, {}, {
              responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `storyboard-export-${qualificationId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            setExportToast({ open: true, message: '✅ PDF downloaded successfully!', severity: 'success' });
          } catch (e) {
            setExportToast({ open: true, message: '❌ PDF export failed: ' + (e.response?.data?.message || e.message), severity: 'error' });
          }
        }}>Export PDF</Button>
        <Button variant="outlined" size="small" color="primary" onClick={async () => {
          try {
            setExportToast({ open: true, message: 'Generating Word document...', severity: 'info' });
            const response = await api.post(`/qualifications/${qualificationId}/storyboard-nodes/export-word`, {}, {
              responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `storyboard-export-${qualificationId}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            setExportToast({ open: true, message: '✅ Word document downloaded successfully!', severity: 'success' });
          } catch (e) {
            setExportToast({ open: true, message: '❌ Word export failed: ' + (e.response?.data?.message || e.message), severity: 'error' });
          }
        }}>Export Word</Button>
      </Stack>
      <Box sx={containerSx}>
        <ReactFlow
          nodes={nodesWithProps}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          defaultViewport={{ x: -800, y: -300, zoom: 0.35 }}
          minZoom={0.1}
          maxZoom={2.5}
          zoomOnScroll={zoomEnabled}
          panOnScroll={false}
          connectionLineType="smoothstep"
          fitView
          fitViewOptions={{ padding: 0.3 }}
          proOptions={{ hideAttribution: true }}
          style={{ background: '#0f0f12' }}
          defaultEdgeOptions={{ type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#7c3aed' }, style: { stroke: '#7c3aed', strokeWidth: 2 } }}
          nodeTypes={nodeTypes}
          onNodeMouseEnter={() => setZoomEnabled(false)}
          onNodeMouseLeave={() => setZoomEnabled(true)}
        >
          <Background variant="dots" gap={20} size={1} color="#1e1e23" />
          <MiniMap />
          <Controls />
        </ReactFlow>
      </Box>

      {/* Tasks Modal */}
      <Dialog open={tasksModal.open} onClose={() => setTasksModal(s => ({ ...s, open: false }))} maxWidth="lg" fullWidth>
        <DialogTitle>Assigned Tasks for {tasksModal.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3, pt: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Knowledge Tasks (KT)</Typography>
              {/* simple recursive view */}
              {Array.isArray(tasksModal.assigned.KT) && tasksModal.assigned.KT.map((it, i) => (
                <Box key={'kt'+i} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{(it.code ? `${it.code}: ` : '')}{it.title || ''}</Typography>
                  {it.text && (<Typography variant="caption" color="text.secondary">{it.text}</Typography>)}
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Practical Skills (PS)</Typography>
              {Array.isArray(tasksModal.assigned.PS) && tasksModal.assigned.PS.map((it, i) => (
                <Box key={'ps'+i} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{(it.code ? `${it.code}: ` : '')}{it.title || ''}</Typography>
                  {it.text && (<Typography variant="caption" color="text.secondary">{it.text}</Typography>)}
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Workplace Experience (WE)</Typography>
              {Array.isArray(tasksModal.assigned.WE) && tasksModal.assigned.WE.map((it, i) => (
                <Box key={'we'+i} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{(it.code ? `${it.code}: ` : '')}{it.title || ''}</Typography>
                  {it.text && (<Typography variant="caption" color="text.secondary">{it.text}</Typography>)}
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
        <DialogTitle>Share Read-Only Storyboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Anyone with this link can view the storyboard, pan/zoom, and export. They cannot edit.
          </Typography>
          <TextField 
            fullWidth 
            value={shareLink} 
            InputProps={{ readOnly: true }} 
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={() => { 
              try { 
                navigator.clipboard.writeText(shareLink);
                setExportToast({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
                setShareDialogOpen(false);
              } catch {} 
            }}
          >
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Toast - Dismissible */}
      <Snackbar 
        open={progressToast.open} 
        autoHideDuration={null}
        onClose={() => setProgressToast(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={progressToast.completed === progressToast.total ? 'success' : 'info'}
          onClose={() => setProgressToast(s => ({ ...s, open: false }))}
          sx={{ minWidth: 400 }}
        >
          {progressToast.message}
        </Alert>
      </Snackbar>

      {/* Export Toast */}
      <Snackbar 
        open={exportToast.open} 
        autoHideDuration={4000}
        onClose={() => setExportToast(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={exportToast.severity}
          onClose={() => setExportToast(s => ({ ...s, open: false }))}
        >
          {exportToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function StoryBoardFlow({ qualificationId }) {
  return (
    <ReactFlowProvider>
      <StoryBoardFlowInner qualificationId={qualificationId} />
    </ReactFlowProvider>
  );
}


